import React, { useEffect, useState } from "react";
import {
  Box,
  Modal,
  Typography,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableFooter,
  TablePagination,
  LinearProgress,
} from "@mui/material";
import { ModalDrawerStyles, ModalHeader } from "../Common/styles/modal";
import {
  NoDataContainer,
  pageSize,
  paginationLabel,
  StyledTableCell,
  TableBorderRadiusTopLeftRight,
  TablePaginationStyle,
} from "../Common/styles/table";
import { errorToastMessage } from "../../utils/toast";
import StatusBadge from "../Common/UI/StatusTag";
import http from "../../utils/http";
import { DateTime } from "luxon";

type VersionHistoryItem = {
  id: string;
  version: string;
  status: "draft" | "active" | "inactive";
  startAndEndDate: string;
};

type Props = {
  showModal: boolean;
  closeModal: () => void;
  consentName: string;
  consentSummary: string;
  studyId: string;
  consentId: string;
  type: "platform" | "study";
  formType?: "consent" | "sunshine";
};

const statusMap: any = {
  draft: { label: "Draft", variant: "completed" },
  active: { label: "Active", variant: "active" },
  inactive: { label: "Inactive", variant: "cancelled" },
};

const EConsentVersionHistoryModal = ({
  showModal,
  closeModal,
  consentName,
  consentSummary,
  studyId,
  consentId,
  type,
  formType,
}: Props) => {
  const [versions, setVersions] = useState<VersionHistoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [dataCount, setDataCount] = useState(0);

  useEffect(() => {
    const fetchVersions = async () => {
      try {
        setLoading(true);
        const prefix =
          type === "platform"
            ? `/platform/admin/econsent`
            : formType === "sunshine"
            ? `/admin/study/${studyId}/sunshine`
            : `/admin/study/${studyId}/econsent`;
        const res = await http.get(
          `${prefix}/${consentId}/versions?page=${page + 1}&size=${pageSize}`
        );
        const formattedData = res.data?.data?.data?.map((version: any) => ({
          id: version.id,
          version: version.version,

          status: version.status || "draft",
          startAndEndDate: version.startDate
            ? version.endDate
              ? `${DateTime.fromISO(version.startDate).toFormat(
                  "MMM dd, yyyy"
                )} - ${DateTime.fromISO(version.endDate).toFormat(
                  "MMM dd, yyyy"
                )}`
              : `${DateTime.fromISO(version.startDate).toFormat(
                  "MMM dd, yyyy"
                )}`
            : "",
        }));
        setVersions(formattedData || []);
        setDataCount(res.data?.data?.meta?.paginationOptions?.totalItems ?? 0);
        setLoading(false);
      } catch (err) {
        setLoading(false);
        errorToastMessage(err as Error);
      }
    };
    if (showModal) {
      fetchVersions();
    }
  }, [studyId, consentId, showModal, page, type, formType]);

  const handleChangePage = (_1: any, newPage: number) => {
    setPage(newPage);
  };

  return (
    <Modal open={showModal} onClose={closeModal}>
      <Box sx={ModalDrawerStyles}>
        <ModalHeader title="Version History" onCloseClick={closeModal} />
        <Box sx={{ pt: 2 }}>
          <Box sx={{ mb: 3, display: "flex", gap: 4 }}>
            <Box>
              <Typography
                variant="subtitle1"
                fontWeight={"regular"}
                sx={{ mb: 1 }}
              >
                {formType === "consent" ? "Consent Name" : "Form Name"}
              </Typography>
              <Typography variant="subtitle1">{consentName || "-"}</Typography>
            </Box>
            <Box>
              <Typography
                variant="subtitle1"
                fontWeight={"regular"}
                sx={{ mb: 1 }}
              >
                {formType === "consent"
                  ? "Consent Summary"
                  : "Form Description"}
              </Typography>
              <Typography variant="subtitle1">
                {consentSummary || "-"}
              </Typography>
            </Box>
          </Box>

          <Table sx={TableBorderRadiusTopLeftRight}>
            <TableHead>
              <TableRow>
                <StyledTableCell>Version</StyledTableCell>
                <StyledTableCell>Start and End Date</StyledTableCell>
                <StyledTableCell>Status</StyledTableCell>
              </TableRow>
            </TableHead>
            {!loading && versions.length > 0 && (
              <>
                <TableBody>
                  {versions.map((version: VersionHistoryItem) => (
                    <TableRow key={version.id}>
                      <StyledTableCell>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: "regular",
                            color: "text.primary",
                          }}
                        >
                          {version.version || "-"}
                        </Typography>
                      </StyledTableCell>
                      <StyledTableCell>
                        {version.startAndEndDate || "-"}
                      </StyledTableCell>
                      <StyledTableCell>
                        <StatusBadge
                          label={
                            statusMap[version?.status]?.label ?? version?.status
                          }
                          variant={statusMap[version?.status]?.variant}
                        />
                      </StyledTableCell>
                    </TableRow>
                  ))}
                </TableBody>
                {dataCount > pageSize && (
                  <TableFooter>
                    <TableRow>
                      <TablePagination
                        sx={TablePaginationStyle}
                        count={dataCount}
                        page={page}
                        rowsPerPage={pageSize}
                        rowsPerPageOptions={[pageSize]}
                        onPageChange={handleChangePage}
                        labelDisplayedRows={paginationLabel}
                      />
                    </TableRow>
                  </TableFooter>
                )}
              </>
            )}
          </Table>
          {!loading && versions.length === 0 && (
            <NoDataContainer>
              <Typography variant="body1" color="gray">
                No versions found
              </Typography>
            </NoDataContainer>
          )}
          {loading && (
            <Box sx={{ width: "100%" }}>
              <LinearProgress />
            </Box>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default EConsentVersionHistoryModal;
